import type { DelayBroadcastGranularity, DelayBroadcastPattern } from '../../../api/types';
import { localizedDelayDefaults, type DelayTemplateKind } from '../../../utils/localizedSsml';
import { LocalizedTextFields } from '../parts/LocalizedTextFields';
import {
  DELAY_BROADCAST_GRANULARITIES,
  GRANULARITY_DEFAULT_SSML,
  resolveEffectiveSsml,
  resolveLocalizedDefaults,
  type DefaultSsmlResolver,
} from '../../../utils/broadcastContents';
import { granularityPhraseTemplateId } from '../../../data/phraseTemplates';
import { usePhraseTemplateDefaults } from '../../../contexts/PhraseTemplateContext';
// 数値入力は共通の NumericInput を使う。
// 素の <input type="number" value={number}> だと Number('') === 0 のため
// 既定値 0 を消せない不具合が起きる (NumericInput 側のコメント参照)。
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';

export function DelayPatternRow({
  index,
  pattern,
  languages = ['ja'],
  canRemove,
  ssmlTestIdPrefix = 'self-train-delay-ssml-template',
  placeholderTemplates = GRANULARITY_DEFAULT_SSML,
  templateKind = 'self',
  granularityTemplatePrefix,
  fixedTemplateId,
  hideGranularity = false,
  defaultSsmlResolver,
  previewSlot,
  highlighted = false,
  invalid = false,
  onUpdate,
  onAdd,
  onRemove,
}: {
  index: number;
  pattern: DelayBroadcastPattern;
  languages?: readonly string[];
  /** 削除可能か (最後の 1 行のときは false。false のとき「−」ボタンを表示しない) */
  canRemove: boolean;
  /**
   * SSML テンプレート <textarea> に付与する data-testid のプレフィックス。
   * 自列車遅延と接続線遅延 (transfer_aware) の両方で同じ行コンポーネントを再利用するため、
   * 呼び出し元で区別できるように `<prefix>-<index>` の testid が割り当てられる。
   * デフォルトは自列車遅延の従来 testid (`self-train-delay-ssml-template-{index}`) を維持する。
   */
  ssmlTestIdPrefix?: string;
  /**
   * 放送粒度ごとのデフォルト SSML 文言 (textarea の placeholder に使われる)。
   * 自列車遅延と接続線遅延 (transfer_aware) で文面が異なるため、呼び出し元から
   * 注入できるようにする。デフォルトは自列車遅延の文言
   * (`GRANULARITY_DEFAULT_SSML` / Lambda の `GRANULARITY_DEFAULT_TEMPLATE` と一致)。
   * 接続線遅延の表形式エディタからは `CONNECTING_GRANULARITY_DEFAULT_SSML` を渡し、
   * Lambda の `CONNECTING_GRANULARITY_DEFAULT_TEMPLATE` と一致した
   * 「<駅名>駅からお乗換の<線名>... 現在 N 分ほど遅れています」系の文面を表示する。
   */
  placeholderTemplates?: Record<DelayBroadcastGranularity, string>;
  templateKind?: DelayTemplateKind;
  /**
   * 空欄時の「実効文面」を解決するための定型文マスタ ID 接頭辞 (Issue #527)。
   *
   * 指定すると placeholder が **定型文マスタの文面** (`defaultSsmlResolver` 経由) に
   * 切り替わり、「未入力ならこの文面が放送される」ことを正しく提示できる。
   * 未指定なら `placeholderTemplates` (コード内既定) をそのまま使う。
   */
  granularityTemplatePrefix?: string;
  /**
   * 粒度に依らない **単一の** 定型文マスタ ID (SBI-5 / 発車見込み案内)。
   *
   * 発車見込み案内は粒度別のマスタを持たない (`connecting_delay_departure_forecast` 等)。
   * 指定すると `granularityTemplatePrefix` より優先して、この ID で placeholder /
   * 多言語既定文面を解決する。
   */
  fixedTemplateId?: string;
  /**
   * 「放送粒度」列を隠す (SBI-5 / 発車見込み案内)。
   *
   * 発車見込み案内は遅延分数を読み上げないため粒度が文面に影響しない。
   * 粒度は呼び出し元が固定値で保存する (表示しないだけでデータは持つ)。
   * 表の見出し (`<th>`) も呼び出し元で同じ条件で隠すこと。
   */
  hideGranularity?: boolean;
  /**
   * 定型文マスタ (`GET /phrase-templates`) から作った既定文面リゾルバ (Issue #527)。
   *
   * 未指定 / マスタ未登録なら `placeholderTemplates` (コード内既定) へフォールバックする
   * ため、API 取得に失敗しても placeholder が空にならない。
   */
  defaultSsmlResolver?: DefaultSsmlResolver;
  /**
   * 行ごとの音声プレビュー UI (Issue #439)。
   *
   * SSML 入力欄の直下に差し込む。「凡例がある欄 = プレビューしたい欄」なので、
   * 行単位で「▶ この文面を試聴」を置くのが直感的。
   * 呼び出し元が `AudioPreviewField` を組み立てて渡す (本コンポーネントは
   * プレビューの実装詳細を知らずレイアウトだけを担う)。
   */
  previewSlot?: React.ReactNode;
  /**
   * プレビューで実際に採用された行かどうか (Issue #439)。
   *
   * サーバの `select_delay_pattern()` が選んだ行を強調表示し、
   * 「境界値でどのパターンが鳴るか」を保存前に目で確認できるようにする。
   */
  highlighted?: boolean;
  /**
   * この行にバリデーションエラーがあるか (入力エラーの「見つけやすさ」対応)。
   *
   * `true` のとき SSML 入力欄に `.is-invalid` (赤枠) を付け、行全体にも
   * 左端の赤いバーを出して「表のどの行が原因か」を一目で分かるようにする。
   * 遅延パターンは行数が多くなりがちで、エラー文言の「パターン3」だけでは
   * 目で数えて探す必要があったため、行単位の視覚表示を加える。
   */
  invalid?: boolean;
  onUpdate: (patch: Partial<DelayBroadcastPattern>) => void;

  onAdd: () => void;
  onRemove: () => void;
}): React.JSX.Element {
  const granularity = pattern.granularity as DelayBroadcastGranularity;

  /*
   * Issue #552: 定型文マスタの既定文面リゾルバ。
   *
   * props で明示的に渡された場合はそれを優先し (単体テストからの注入用)、
   * 未指定ならコンテキスト (`PhraseTemplateProvider`) から取得する。
   * どちらも無ければ `undefined` = コード内既定文面へフォールバックする。
   */
  const contextResolver = usePhraseTemplateDefaults();
  const resolveDefault = defaultSsmlResolver ?? contextResolver;

  /*
   * この行に対応する定型文マスタの `template_id`。
   *
   * `granularityTemplatePrefix` が未指定の呼び出し元 (マスタ委譲の対象外) では
   * `undefined` にして、placeholder / 多言語既定値を従来のコード内既定に保つ。
   */
  const templateId =
    fixedTemplateId ??
    (granularityTemplatePrefix
      ? granularityPhraseTemplateId(granularityTemplatePrefix, granularity)
      : undefined);

  /*
   * 多言語欄 (en / zh / ko) の既定文面もマスタを反映する (Issue #552)。
   *
   * 「多言語の既定文面も固定値で、定型文マスタの多言語設定が確認できない」という
   * 報告に対応する。**当該言語がマスタに登録されている場合のみ** 上書きし、
   * 未登録の言語はコード内既定 (`localizedDelayDefaults`) のまま残す
   * (言語をまたぐフォールバックをすると英語欄に日本語が入ってしまう)。
   *
   * ⚠️ Issue #556: この既定文面は **placeholder (薄字) 専用** であり、
   * 入力欄の値としては使わない (`defaultsAreValues={false}`)。値として投入すると
   * 個別文面が定型文マスタより優先され、「定型文マスタで多言語を整備しても
   * 放送に反映されない」状態に戻る。
   */
  const localizedDefaults = templateId
    ? resolveLocalizedDefaults(
        localizedDelayDefaults(templateKind, granularity),
        templateId,
        resolveDefault,
      )
    : localizedDelayDefaults(templateKind, granularity);

  return (
    <tr
      style={{
        verticalAlign: 'top',
        borderTop: '1px solid #ddd',
        // 採用行のハイライト。背景色 + 左端のバーで「この行が鳴った」ことを示す。
        // エラー行 (invalid) は赤系を優先する。保存できない原因の提示が
        // 「どの行が鳴るか」の情報より優先度が高いため。
        background: invalid ? '#fdf0ef' : highlighted ? '#fff8c5' : undefined,
        boxShadow: invalid
          ? 'inset 3px 0 0 var(--color-danger, #d92c20)'
          : highlighted
            ? 'inset 3px 0 0 #9a6700'
            : undefined,
      }}
      data-testid={`delay-pattern-row-${ssmlTestIdPrefix}-${index}`}
      data-matched={highlighted ? 'true' : undefined}
      // E2E / Vitest から「エラー行がどれか」を検証できるようにする。
      data-invalid={invalid ? 'true' : undefined}
    >
      {/* 遅れ分数 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
          {/*
            遅れ開始分数は NumericInput を使う。
            素の <input type="number" value={number}> だと Number('') === 0 のため
            既定値 0 を消せない (詳細は NumericInput のコメント参照)。
          */}
          <NumericInput
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ開始分数`}
            value={pattern.delay_minutes_from}
            normalizeValue={clampNonNegativeInt}
            onChange={(delayMinutesFrom) => onUpdate({ delay_minutes_from: delayMinutesFrom })}
          />
          <span>分以上〜</span>
          {/*
            遅れ終了分数は「空欄 = 上限なし (undefined)」を正規の値として扱うため、
            NumericInput ではなく素の input を使う (空欄が意味を持つので 0 に戻さない)。
          */}
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ終了分数`}
            value={pattern.delay_minutes_to ?? ''}
            placeholder="上限なし"
            onChange={(e) => {
              const val = e.target.value;
              onUpdate({
                delay_minutes_to: val === '' ? undefined : Math.max(0, Math.floor(Number(val))),
              });
            }}
          />

          <span>分未満の遅れ</span>
        </div>
      </td>

      {/* 放送粒度 (発車見込み案内では列ごと非表示) */}
      {hideGranularity ? null : (
        <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
          <select
            aria-label={`パターン${index + 1} 放送粒度`}
            value={granularity}
            onChange={(event) => {
              const nextGranularity = event.target.value as DelayBroadcastGranularity;
              /*
               * Issue #556: 粒度を変えても **言語別文面へ値を書き込まない**。
               *
               * 以前は切替のたびに各言語の既定訳を `ssml_template_i18n` へ投入していたが、
               * 個別文面は定型文マスタより優先されるため、粒度を触っただけで
               * 「定型文マスタの多言語編集が以後反映されない」状態になっていた。
               * 未入力時に実際に放送される文面は placeholder (薄字) が示すので、
               * ここでは粒度だけを更新する。
               *
               * ⚠️ 既に入力済みの言語別文面 (運用者が書いた訳文) は温存する
               * (粒度を変えたら訳文が消える、という破壊的挙動を避ける)。
               */
              onUpdate({ granularity: nextGranularity });
            }}
          >
            {DELAY_BROADCAST_GRANULARITIES.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </td>
      )}

      {/* 放送パターン (SSMLテンプレート) */}
      <td style={{ padding: '6px', width: '100%' }}>
        {/*
         * data-testid を付与する理由:
         * placeholder には粒度別のデフォルト SSML 文 (例: "<speak>...</speak>") が
         * 動的に入るため、page-summary が拾うラベル (placeholder 優先) が長文化し、
         * AI エージェントが基本情報の「説明」欄 (data-testid=description-input) と
         * 取り違える事象が報告された (Claude が長考の末「{誤った action} 申し訳ありません
         * {正しい action}」と自己訂正レスポンスを返す原因になる)。
         * 明示的な data-testid を付けることで page-summary でも安定したラベル
         * (`self-train-delay-ssml-template-{index}`) として現れ、E2E シナリオからも
         * data-testid 単独で一意に拾えるようにする。
         */}
        {languages.includes('ja') ? (
          <textarea
            rows={2}
            style={{ width: '100%', boxSizing: 'border-box' }}
            aria-label={`パターン${index + 1} 放送パターン`}
            data-testid={`${ssmlTestIdPrefix}-${index}`}
            className={invalid ? 'is-invalid' : undefined}
            // Issue #527: 空欄 = 定型文マスタへ委譲なので **必須にしない**。
            // 必須にすると実値を焼き込む運用に戻ってしまい、
            // 「マスタ編集で既存の空欄コンテンツの放送内容が一括で変わる」が成立しない。
            // 未入力時に実際に読み上げられる文面は placeholder で提示する。
            value={pattern.ssml_template ?? ''}
            // Issue #527: 未入力時に実際に読み上げられる文面を placeholder で提示する。
            // 定型文マスタに登録があればその文面、無ければコード内既定へフォールバック
            // (放送側 `resolve_granularity_template()` と同じ 3 段解決を画面上でも再現)。
            placeholder={
              templateId
                ? resolveEffectiveSsml(
                    undefined,
                    templateId,
                    placeholderTemplates[granularity],
                    resolveDefault,
                  )
                : placeholderTemplates[granularity]
            }
            onChange={(e) =>
              // 空文字入力 (ユーザがクリアしてゼロベース入力に入った状態) もそのまま反映する。
              // ※ バリデーション (`validateContent`) は空文字をエラーとして検出する。
              onUpdate({ ssml_template: e.target.value })
            }
          />
        ) : null}
        <LocalizedTextFields
          label={`パターン${index + 1} 放送パターン`}
          languages={languages}
          value={pattern.ssml_template_i18n}
          // Issue #552: 各言語の既定文面も定型文マスタを反映する
          // (マスタ未登録の言語はコード内既定のまま)。
          defaults={localizedDefaults}
          // Issue #556: 既定文面は **薄字 (placeholder) 専用**。値として入れると
          // 定型文マスタの多言語編集が反映されなくなる (空欄 = マスタ委譲を守る)。
          defaultsAreValues={false}
          // Issue #492 (SBI-11): 「日本語から翻訳」ボタンの翻訳元は同じ行の
          // 日本語テンプレート (`ssml_template`)。
          //
          // testId は `ssmlTestIdPrefix` (呼び出し元が接続線 index まで含めて一意に
          // 与えている) + 行 index で組み立てる。`templateKind` だけでは part2 の
          // 「接続線 A の 1 行目」と「接続線 B の 1 行目」が同一 testid になり、
          // E2E / テストが目的のボタンを特定できない。
          sourceText={pattern.ssml_template ?? ''}
          translateTestId={`${ssmlTestIdPrefix}-${index}`}
          onChange={(value) => onUpdate({ ssml_template_i18n: value })}
        />
        {/*
          Issue #439: SSML 入力欄の直下に音声プレビューを置く。
          SSML を書いた欄 = プレビューしたい欄なので、
          テンプレートを書いた直後にその場で試聴できる位置が最も自然。
        */}
        {previewSlot}
      </td>

      {/* 操作 (行の追加・削除) */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            type="button"
            className="delay-pattern-add"
            aria-label={`パターン${index + 1} の後に行を追加`}
            title="行を追加"
            onClick={onAdd}
          >
            ＋
          </button>
          {canRemove ? (
            <button
              type="button"
              className="delay-pattern-remove danger"
              aria-label={`パターン${index + 1} を削除`}
              title="行を削除"
              onClick={onRemove}
            >
              −
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
